import React, { useRef, useState } from "react";
import api, { mediaUrl } from "../../lib/api";
import { HiSparkles } from "react-icons/hi2";

export default function ImageUploader({
  value,
  onChange,
  label = "Image",
  accept = "image/*",
  aiEnabled = false,
  aiContext = null, // { title, excerpt, tags }
  hint,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiStyle, setAiStyle] = useState("editorial-dark");
  const [aiSize, setAiSize] = useState("1536x1024");
  const [aiQuality, setAiQuality] = useState("medium");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = ""; // allow re-upload of same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  const generateAI = async () => {
    setBusy(true);
    setError(null);
    try {
      const ctx = aiContext || {};
      const { data } = await api.post("/api/admin/ai/cover", {
        title: ctx.title,
        excerpt: ctx.excerpt,
        tags: ctx.tags,
        style: aiStyle,
        size: aiSize,
        quality: aiQuality,
        brand: "Ozzy",
      });
      onChange(data.url);
      setAiOpen(false);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = value ? mediaUrl(value) : null;

  return (
    <div className="field">
      <label>{label}</label>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        style={{
          border: "1px dashed var(--border-strong)",
          borderRadius: 14,
          padding: 14,
          background: "var(--surface)",
        }}
      >
        {previewUrl ? (
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            <img
              src={previewUrl}
              alt="preview"
              style={{
                width: 200, height: 130, objectFit: "cover", objectPosition: "top",
                borderRadius: 10, border: "1px solid var(--border)",
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: "var(--muted)", wordBreak: "break-all", marginBottom: 8 }}>
                {value}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="chip" style={{ cursor: "pointer" }} onClick={() => fileRef.current?.click()} disabled={busy}>
                  Replace
                </button>
                <button type="button" className="chip" style={{ cursor: "pointer", color: "var(--danger)", borderColor: "rgba(255,107,107,0.3)" }} onClick={() => onChange("")}>
                  Remove
                </button>
                {aiEnabled && (
                  <button type="button" className="chip" style={{ cursor: "pointer" }} onClick={() => setAiOpen((v) => !v)}>
                    <HiSparkles /> AI cover
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "12px 4px" }}>
            <button type="button" className="btn" onClick={() => fileRef.current?.click()} disabled={busy}>
              {busy ? "Uploading…" : "Upload file"}
            </button>
            {aiEnabled && (
              <button type="button" className="btn btn--ghost" onClick={() => setAiOpen((v) => !v)} disabled={busy}>
                <HiSparkles /> Generate with AI
              </button>
            )}
            <span className="text-muted" style={{ fontSize: 13 }}>
              or drag & drop a file here
            </span>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept={accept}
          onChange={onFile}
          style={{ display: "none" }}
        />

        {hint && <small style={{ display: "block", color: "var(--muted)", fontSize: 12, marginTop: 8 }}>{hint}</small>}

        {aiOpen && (
          <div
            style={{
              marginTop: 14, padding: 14, background: "var(--bg)",
              border: "1px solid var(--border)", borderRadius: 12,
            }}
          >
            <div className="row-3">
              <div className="field">
                <label>Style</label>
                <select className="select" value={aiStyle} onChange={(e) => setAiStyle(e.target.value)}>
                  <option value="editorial-dark">Editorial dark</option>
                  <option value="tech-3d">Tech 3D render</option>
                  <option value="abstract-gradient">Abstract gradient</option>
                  <option value="code-aesthetic">Code aesthetic</option>
                </select>
              </div>
              <div className="field">
                <label>Size</label>
                <select className="select" value={aiSize} onChange={(e) => setAiSize(e.target.value)}>
                  <option value="1536x1024">1536×1024 (wide)</option>
                  <option value="1024x1024">1024×1024 (square)</option>
                  <option value="1024x1536">1024×1536 (tall)</option>
                </select>
              </div>
              <div className="field">
                <label>Quality</label>
                <select className="select" value={aiQuality} onChange={(e) => setAiQuality(e.target.value)}>
                  <option value="low">Low (~$0.011)</option>
                  <option value="medium">Medium (~$0.04)</option>
                  <option value="high">High (~$0.17)</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn" onClick={generateAI} disabled={busy}>
                {busy ? "Generating…" : "Generate"}
              </button>
              <button type="button" className="chip" style={{ cursor: "pointer" }} onClick={() => setAiOpen(false)}>
                Cancel
              </button>
            </div>
            <small style={{ display: "block", marginTop: 10, color: "var(--muted)", fontSize: 12 }}>
              Uses post title, excerpt and tags as the prompt context.
              Generated image is saved as a new file you can swap any time.
            </small>
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}
      </div>
    </div>
  );
}
